import { useState, useEffect, useCallback, useRef } from "react";

type Person = {
  id: string;
  name: string;
  birthDate: string | null;
  deathDate: string | null;
  bio: string | null;
  imageUrl: string | null;
  notes: string | null;
};

type CharacterScene = {
  sceneId: string;
  sceneCode: string;
  title: string;
  status: "draft" | "approved" | "killed" | "shot";
};

type Audition = {
  id: string;
  personId: string;
  actorName: string;
  link: string | null;
  notes: string | null;
  status: "submitted" | "reviewing" | "callback" | "cast" | "passed";
  createdAt: string;
  updatedAt: string;
};

type Props = {
  personId: string;
  canEdit: boolean;
  onClose: () => void;
  onPersonUpdated?: () => void;
};

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  draft: { bg: "bg-gray-500/20", text: "text-gray-400", border: "border-gray-500/40" },
  approved: { bg: "bg-teal-500/20", text: "text-teal-400", border: "border-teal-500/40" },
  killed: { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/40" },
  shot: { bg: "bg-green-500/20", text: "text-green-400", border: "border-green-500/40" },
};

const AUDITION_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  submitted: { bg: "bg-gray-500/20", text: "text-gray-400" },
  reviewing: { bg: "bg-blue-500/20", text: "text-blue-400" },
  callback: { bg: "bg-amber-500/20", text: "text-amber-400" },
  cast: { bg: "bg-green-500/20", text: "text-green-400" },
  passed: { bg: "bg-red-500/20", text: "text-red-400" },
};

const AUDITION_STATUSES = ["submitted", "reviewing", "callback", "cast", "passed"] as const;

export function CharacterManagerModal({ personId, canEdit, onClose, onPersonUpdated }: Props) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [person, setPerson] = useState<Person | null>(null);
  const [scenes, setScenes] = useState<CharacterScene[]>([]);
  const [auditions, setAuditions] = useState<Audition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Editable notes
  const [notes, setNotes] = useState("");

  // Load data
  useEffect(() => {
    async function load() {
      try {
        setIsLoading(true);
        const res = await fetch(`/api/characters/${personId}`, { credentials: "include" });
        if (!res.ok) throw new Error("Failed to load character");
        const data = await res.json();
        setPerson(data.person);
        setScenes(data.scenes);
        setAuditions(data.auditions);
        setNotes(data.person.notes || "");
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, [personId]);

  // Click outside / Escape
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const handleSaveNotes = useCallback(async () => {
    if (!canEdit) return;
    setIsSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/characters/${personId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ notes: notes || null }),
      });
      if (!res.ok) throw new Error("Failed to save notes");
      onPersonUpdated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  }, [canEdit, personId, notes, onPersonUpdated]);

  const handleAddAudition = useCallback(async () => {
    if (!canEdit) return;
    try {
      const res = await fetch(`/api/characters/${personId}/auditions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ actorName: "New Actor" }),
      });
      if (!res.ok) throw new Error("Failed to add audition");
      const data = await res.json();
      setAuditions((prev) => [...prev, data.audition]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add audition");
    }
  }, [canEdit, personId]);

  const handleUpdateAudition = useCallback(async (auditionId: string, updates: Partial<Audition>) => {
    if (!canEdit) return;
    try {
      const res = await fetch(`/api/characters/${personId}/auditions/${auditionId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Failed to update audition");
      const data = await res.json();
      setAuditions((prev) => prev.map((a) => (a.id === auditionId ? data.audition : a)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update audition");
    }
  }, [canEdit, personId]);

  const handleDeleteAudition = useCallback(async (auditionId: string) => {
    if (!canEdit) return;
    try {
      const res = await fetch(`/api/characters/${personId}/auditions/${auditionId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete audition");
      setAuditions((prev) => prev.filter((a) => a.id !== auditionId));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete audition");
    }
  }, [canEdit, personId]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "?";
    return new Date(dateStr).getFullYear().toString();
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
        <div ref={modalRef} className="bg-surface border border-border rounded-lg shadow-2xl p-8">
          <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
        <div ref={modalRef} className="bg-surface border border-border rounded-lg shadow-2xl p-8">
          <p className="text-red-400 text-sm">Character not found</p>
          <button onClick={onClose} className="mt-4 text-primary-400 text-sm">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div
        ref={modalRef}
        className="bg-surface border border-border rounded-lg shadow-2xl overflow-hidden"
        style={{ width: 640, maxHeight: "85vh" }}
      >
        {/* Header */}
        <div className="flex items-center gap-4 px-5 py-4 border-b border-border bg-surface-elevated">
          {person.imageUrl ? (
            <img
              src={person.imageUrl}
              alt={person.name}
              className="w-12 h-12 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-12 h-12 bg-surface rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-muted text-lg font-medium">
                {person.name[0].toUpperCase()}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-foreground font-copernicus text-lg truncate">{person.name}</h2>
            <p className="text-muted text-xs">
              {formatDate(person.birthDate)} &ndash; {formatDate(person.deathDate)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-muted hover:text-foreground hover:bg-border rounded transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-5 mt-3 px-2 py-1.5 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-xs">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="p-5 space-y-5 overflow-y-auto" style={{ maxHeight: "calc(85vh - 80px)" }}>
          {/* Production Notes */}
          <div>
            <label className="block text-[10px] text-muted mb-1.5 uppercase tracking-wider">Production Notes</label>
            {canEdit ? (
              <div className="space-y-2">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes about this character for production..."
                  className="w-full bg-surface-elevated border border-border rounded px-3 py-2 text-sm text-foreground placeholder:text-muted resize-none"
                  style={{ minHeight: 100 }}
                />
                <div className="flex justify-end">
                  <button
                    onClick={handleSaveNotes}
                    disabled={isSaving}
                    className="px-3 py-1.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-xs font-medium rounded transition-colors"
                  >
                    {isSaving ? "Saving..." : "Save Notes"}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-foreground/80 whitespace-pre-wrap p-3 bg-surface-elevated rounded border border-border" style={{ minHeight: 60 }}>
                {notes || "No production notes"}
              </p>
            )}
          </div>

          {/* Scene Appearances */}
          <div>
            <label className="block text-[10px] text-muted mb-1.5 uppercase tracking-wider">
              Scene Appearances ({scenes.length})
            </label>
            {scenes.length === 0 ? (
              <p className="text-xs text-muted italic">Not in any scenes</p>
            ) : (
              <div className="border border-border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-elevated text-muted text-[10px] uppercase tracking-wider">
                      <th className="text-left px-3 py-2">Code</th>
                      <th className="text-left px-3 py-2">Title</th>
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {scenes.map((s) => {
                      const sc = STATUS_COLORS[s.status] || STATUS_COLORS.draft;
                      return (
                        <tr key={s.sceneId} className="hover:bg-surface-elevated/50">
                          <td className="px-3 py-2 text-foreground font-medium">{s.sceneCode}</td>
                          <td className="px-3 py-2 text-foreground">{s.title}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${sc.bg} ${sc.text}`}>
                              {s.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Auditions */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-[10px] text-muted uppercase tracking-wider">
                Auditions ({auditions.length})
              </label>
              {canEdit && (
                <button
                  onClick={handleAddAudition}
                  className="text-[10px] text-primary-400 hover:text-primary-300 transition-colors"
                >
                  + Add
                </button>
              )}
            </div>
            {auditions.length === 0 ? (
              <p className="text-xs text-muted italic">No auditions tracked</p>
            ) : (
              <div className="border border-border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-elevated text-muted text-[10px] uppercase tracking-wider">
                      <th className="text-left px-3 py-2">Actor</th>
                      <th className="text-left px-3 py-2 w-10">Link</th>
                      <th className="text-left px-3 py-2">Notes</th>
                      <th className="text-left px-3 py-2 w-28">Status</th>
                      {canEdit && <th className="w-8 px-2 py-2"></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {auditions.map((a) => {
                      const asc = AUDITION_STATUS_COLORS[a.status] || AUDITION_STATUS_COLORS.submitted;
                      return (
                        <tr key={a.id} className="hover:bg-surface-elevated/50">
                          <td className="px-3 py-2">
                            {canEdit ? (
                              <input
                                type="text"
                                value={a.actorName}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setAuditions((prev) =>
                                    prev.map((au) => au.id === a.id ? { ...au, actorName: val } : au)
                                  );
                                }}
                                onBlur={() => handleUpdateAudition(a.id, { actorName: a.actorName })}
                                className="bg-transparent border-none text-foreground text-sm focus:outline-none focus:ring-0 w-full"
                              />
                            ) : (
                              <span className="text-foreground">{a.actorName}</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {canEdit ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={a.link || ""}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setAuditions((prev) =>
                                      prev.map((au) => au.id === a.id ? { ...au, link: val || null } : au)
                                    );
                                  }}
                                  onBlur={() => handleUpdateAudition(a.id, { link: a.link })}
                                  placeholder="URL"
                                  className="bg-transparent border-none text-foreground text-sm focus:outline-none focus:ring-0 w-full min-w-0"
                                />
                                {a.link && (
                                  <a
                                    href={a.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-400 hover:text-primary-300 flex-shrink-0"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </a>
                                )}
                              </div>
                            ) : (
                              a.link ? (
                                <a
                                  href={a.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-400 hover:text-primary-300"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              ) : (
                                <span className="text-muted">-</span>
                              )
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {canEdit ? (
                              <input
                                type="text"
                                value={a.notes || ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setAuditions((prev) =>
                                    prev.map((au) => au.id === a.id ? { ...au, notes: val || null } : au)
                                  );
                                }}
                                onBlur={() => handleUpdateAudition(a.id, { notes: a.notes })}
                                placeholder="Notes..."
                                className="bg-transparent border-none text-foreground text-sm focus:outline-none focus:ring-0 w-full placeholder:text-muted"
                              />
                            ) : (
                              <span className="text-foreground">{a.notes || "-"}</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {canEdit ? (
                              <select
                                value={a.status}
                                onChange={(e) => handleUpdateAudition(a.id, { status: e.target.value as Audition["status"] })}
                                className={`px-2 py-0.5 rounded text-xs font-medium ${asc.bg} ${asc.text} border-none bg-transparent focus:outline-none cursor-pointer`}
                              >
                                {AUDITION_STATUSES.map((s) => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            ) : (
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${asc.bg} ${asc.text}`}>
                                {a.status}
                              </span>
                            )}
                          </td>
                          {canEdit && (
                            <td className="px-2 py-2">
                              <button
                                onClick={() => handleDeleteAudition(a.id)}
                                className="text-muted hover:text-red-400 transition-colors p-1"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
